
import React from 'react';
import { Calendar, AlertCircle, TrendingUp, Check, Tag, Layers, Store } from 'lucide-react';
import { Campaign } from '../types';
import { asArray } from '../utils/asArray';

interface CampaignNoteProps {
  campaign: Campaign;
  currencyCode?: string;
  brandNames?: string[];
}

export const CampaignNote: React.FC<CampaignNoteProps> = ({ campaign, currencyCode, brandNames = [] }) => {
  const currency = currencyCode || 'QAR';
  const safeBrands = asArray(brandNames);
  const brandLabel = safeBrands.length > 1 ? 'Brands' : 'Brand';
  const brandDisplay =
    safeBrands.length > 3
      ? `${safeBrands.slice(0, 3).join(', ')} +${safeBrands.length - 3} more`
      : safeBrands.join(', ');
  const safeAggregators = Array.isArray(campaign.aggregators)
    ? campaign.aggregators
    : campaign.aggregators
    ? String(campaign.aggregators)
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean)
    : [];
  return (
    <div className="w-full max-w-2xl glass-panel rounded-2xl p-1 shadow-xl shadow-teal-900/10">
      <div className="bg-gradient-to-b from-white to-zinc-50 rounded-2xl p-6 md:p-8 relative overflow-hidden text-sm">
        <div className="relative z-10">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-[11px] font-bold uppercase tracking-wide shadow-sm">
              <TrendingUp className="w-3 h-3" />
              Active Campaign
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-zinc-900 tracking-tight leading-tight">
              {campaign.name}
            </h2>

            {safeAggregators.length > 0 && (
              <div className="flex flex-col items-center gap-3 w-full">
                <h4 className="text-zinc-400 font-bold text-[11px] uppercase tracking-wider flex items-center justify-center gap-2">
                  <Layers className="w-4 h-4" />
                  Campaign Runs On
                </h4>
                <div className="flex flex-wrap justify-center gap-2 w-full">
                  {safeAggregators.map((agg) => (
                    <span key={agg} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200 rounded-full text-xs font-bold text-zinc-700 shadow-sm">
                      <Check className="w-3.5 h-3.5 text-teal-500" />
                      {agg}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm max-w-xl w-full">
              <p className="text-sm text-zinc-600 leading-relaxed font-medium">
                {campaign.description}
              </p>
            </div>

            <div className="flex items-center gap-2 text-zinc-600 font-semibold bg-zinc-100/80 px-3 py-1.5 rounded-lg text-xs">
              <Calendar className="w-4 h-4 text-zinc-400" />
              {campaign.startDate} - {campaign.endDate}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="group relative bg-teal-50/50 hover:bg-teal-50 rounded-xl p-4 border border-teal-100 transition-colors flex flex-col justify-center items-center text-center">
              <div className="bg-white p-2 rounded-full shadow-sm text-teal-600 mb-2 border border-teal-50">
                <Tag className="w-6 h-6" />
              </div>
              <h4 className="text-zinc-400 font-bold text-[11px] uppercase tracking-wider mb-1">Campaign Discount</h4>
              <div className="text-3xl font-extrabold text-teal-700 tracking-tight flex items-baseline justify-center">
                {campaign.discountValue}
                <span className="text-lg ml-1 font-bold text-teal-600/70">{campaign.discountType === 'Percentage' ? '%' : ` ${currency}`}</span>
              </div>
              <div className="text-teal-600/60 font-bold text-xs mt-1 uppercase tracking-wide">{campaign.discountType} OFF</div>
            </div>

            <div className="group relative bg-white rounded-xl p-4 border border-zinc-100 transition-colors flex flex-col justify-center items-center text-center shadow-sm">
              <div className="bg-teal-50 p-2 rounded-full shadow-sm text-teal-600 mb-2 border border-teal-50">
                <Tag className="w-6 h-6" />
              </div>
              <h4 className="text-zinc-400 font-bold text-[11px] uppercase tracking-wider mb-1">Funding Type</h4>
              <div className="text-2xl font-extrabold text-teal-700 tracking-tight flex items-baseline justify-center">
                {campaign.fundingType || 'None'}
              </div>
              <div className="text-teal-600/60 font-bold text-xs mt-1 uppercase tracking-wide text-center">
                {campaign.fundingType === 'Co-Funded' && (
                  campaign.discountType === 'Percentage'
                    ? <>Sterling {campaign.sterlingPercent ?? '-'}% / Partner {campaign.coFundedPercent ?? '-'}%</>
                    : (() => {
                      const dv = Number(campaign.discountValue) || 0;
                      const s = campaign.sterlingPercent ?? 0;
                      const p = campaign.coFundedPercent ?? 0;
                      return <>Sterling {currency} {(dv * (s / 100)).toFixed(2)} / Partner {currency} {(dv * (p / 100)).toFixed(2)}</>;
                    })()
                )}
                {campaign.fundingType === 'Fully Funded' && (
                  campaign.discountType === 'Percentage'
                    ? <>Sterling {campaign.sterlingPercent ?? 100}%</>
                    : <>Sterling {currency} {(Number(campaign.discountValue) || 0).toFixed(2)}</>
                )}
                {(!campaign.fundingType || campaign.fundingType === 'None') && 'No funding breakdown'}
              </div>
            </div>
          </div>
          
          {campaign.adminWarning && (
            <div className="mt-4 pt-3 border-t border-zinc-100 flex gap-3 items-start text-xs justify-center">
              <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
              <p className="text-zinc-500 leading-relaxed text-left">
                <span className="font-bold text-amber-600 block mb-0.5">Requirement:</span>
                {campaign.adminWarning}
              </p>
            </div>
          )}

          {safeBrands.length > 0 && (
            <div className="mt-6 pt-6 border-t border-zinc-100">
              <h4 className="text-zinc-400 font-bold text-[11px] uppercase tracking-wider mb-4 flex items-center justify-center gap-2">
                <Store className="w-4 h-4" />
                Participating Brands ({safeBrands.length})
              </h4>
              <div className="flex flex-wrap justify-center gap-2">
                {safeBrands.map((brand, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded-lg text-xs font-bold text-zinc-700 transition-colors cursor-default">
                    <Check className="w-3 h-3 text-teal-500" />
                    {brand}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
